import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { FaCheck, FaTimes, FaHourglass, FaCalendar, FaUser, FaTrash } from 'react-icons/fa';

const AdminManagerLeaves = () => {
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [stats, setStats] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });

    useEffect(() => {
        fetchLeaves();
    }, [filter]);

    const fetchLeaves = async () => {
        try {
            setLoading(true);
            const params = filter !== 'all' ? { status: filter } : {};
            const response = await api.get('/leaves/managers', { params });

            if (response.data.success) {
                const leavesData = response.data.data;
                setLeaves(leavesData);

                // Calculate stats
                setStats({
                    all: leavesData.length,
                    pending: leavesData.filter(l => l.status === 'pending').length,
                    approved: leavesData.filter(l => l.status === 'approved').length,
                    rejected: leavesData.filter(l => l.status === 'rejected').length
                });
            }
        } catch (error) {
            toast.error('Failed to fetch manager leaves');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (leaveId, status) => {
        if (!window.confirm(`Are you sure you want to ${status} this leave request?`)) {
            return;
        }

        try {
            const response = await api.put(`/leaves/${leaveId}/status`, { status });

            if (response.data.success) {
                toast.success(`Leave ${status} successfully`);
                fetchLeaves();
            }
        } catch (error) {
            toast.error('Failed to update leave status');
            console.error(error);
        }
    };

    const deleteLeave = async (leaveId) => {
        if (!window.confirm('Are you sure you want to delete this leave request?')) {
            return;
        }

        try {
            const response = await api.delete(`/leaves/${leaveId}`);

            if (response.data.success) {
                toast.success('Leave deleted successfully');
                fetchLeaves();
            }
        } catch (error) {
            toast.error('Failed to delete leave');
            console.error(error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'text-green-600 bg-green-100';
            case 'rejected': return 'text-red-600 bg-red-100';
            case 'pending': return 'text-yellow-600 bg-yellow-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return <FaCheck className="inline mr-1" />;
            case 'rejected': return <FaTimes className="inline mr-1" />;
            case 'pending': return <FaHourglass className="inline mr-1" />;
            default: return null;
        }
    };

    const filteredLeaves = filter === 'all' ? leaves : leaves.filter(l => l.status === filter);

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold">Manager Leave Requests</h1>
                <p className="text-gray-600 mt-2">Manage leave requests from managers</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm">Total Requests</p>
                            <p className="text-3xl font-bold mt-2">{stats.all}</p>
                        </div>
                        <FaCalendar className="text-4xl text-blue-500" />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm">Pending</p>
                            <p className="text-3xl font-bold mt-2 text-yellow-600">{stats.pending}</p>
                        </div>
                        <FaHourglass className="text-4xl text-yellow-500" />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm">Approved</p>
                            <p className="text-3xl font-bold mt-2 text-green-600">{stats.approved}</p>
                        </div>
                        <FaCheck className="text-4xl text-green-500" />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-sm">Rejected</p>
                            <p className="text-3xl font-bold mt-2 text-red-600">{stats.rejected}</p>
                        </div>
                        <FaTimes className="text-4xl text-red-500" />
                    </div>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="bg-white rounded-lg shadow mb-6">
                <div className="flex border-b">
                    {[
                        { key: 'all', label: 'All Requests' },
                        { key: 'pending', label: 'Pending' },
                        { key: 'approved', label: 'Approved' },
                        { key: 'rejected', label: 'Rejected' }
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setFilter(tab.key)}
                            className={`px-6 py-3 font-medium ${filter === tab.key
                                    ? 'border-b-2 border-blue-600 text-blue-600'
                                    : 'text-gray-600 hover:text-blue-600'
                                }`}
                        >
                            {tab.label} ({tab.key === 'all' ? stats.all : stats[tab.key]})
                        </button>
                    ))}
                </div>
            </div>

            {/* Leaves Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <p className="mt-4 text-gray-600">Loading manager leaves...</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Manager
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Leave Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Duration
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Reason
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredLeaves.map(leave => (
                                    <tr key={leave._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                    <FaUser className="text-blue-600" />
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{leave.user?.name}</div>
                                                    <div className="text-sm text-gray-500">{leave.user?.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                                                style={{
                                                    backgroundColor: leave.leaveType?.color + '20',
                                                    color: leave.leaveType?.color
                                                }}
                                            >
                                                {leave.leaveType?.name}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                <div className="flex items-center gap-2">
                                                    <FaCalendar className="text-gray-400" />
                                                    {new Date(leave.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                    {' - '}
                                                    {new Date(leave.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">{leave.days} day{leave.days > 1 ? 's' : ''}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900 max-w-xs truncate" title={leave.reason}>
                                                {leave.reason}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(leave.status)}`}>
                                                {getStatusIcon(leave.status)}
                                                {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex gap-2">
                                                {leave.status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => updateStatus(leave._id, 'approved')}
                                                            className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition"
                                                            title="Approve"
                                                        >
                                                            <FaCheck />
                                                        </button>
                                                        <button
                                                            onClick={() => updateStatus(leave._id, 'rejected')}
                                                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                                                            title="Reject"
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => deleteLeave(leave._id)}
                                                    className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
                                                    title="Delete"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {filteredLeaves.length === 0 && (
                            <div className="text-center py-12">
                                <FaCalendar className="mx-auto text-6xl text-gray-300 mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    No Manager Leave Requests
                                </h3>
                                <p className="text-gray-500">
                                    {filter === 'all'
                                        ? 'No manager leave requests found'
                                        : `No ${filter} manager leave requests`
                                    }
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminManagerLeaves;